import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowRight, Sparkles } from "lucide-react";
import heroTechStack from "@/assets/hero-tech-stack.png";

interface MousePosition {
  x: number;
  y: number;
}

const HeroSection = () => {
  const { t } = useLanguage();
  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState<MousePosition>({ x: 0, y: 0 });
  const sectionRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const clientX = e.clientX;
    const clientY = e.clientY;
    requestAnimationFrame(() => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const x = (clientX - rect.left - rect.width / 2) / (rect.width / 2);
        const y = (clientY - rect.top - rect.height / 2) / (rect.height / 2);
        setMousePosition({ x: x * 0.3, y: y * 0.3 });
      }
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setMousePosition({ x: 0, y: 0 });
  }, []);

  const parallaxOffset = scrollY * 0.4;

  return (
    <section 
      ref={sectionRef}
      className="relative overflow-hidden min-h-[90vh] flex items-center"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* Background with parallax and 3D effect - z-0 to stay behind */}
      <div 
        ref={containerRef}
        className="absolute inset-0 z-0 transition-transform duration-500 ease-out"
        style={{
          transform: `
            perspective(1200px)
            translateY(${parallaxOffset * 0.3}px)
            rotateX(${-mousePosition.y * 3}deg)
            rotateY(${mousePosition.x * 3}deg)
            scale(1.15)
          `,
          transformStyle: "preserve-3d",
        }}
      >
        <img
          src={heroTechStack}
          alt="Technology Stack Architecture"
          className="absolute inset-0 w-full h-full object-cover object-center"
          fetchPriority="high"
        />
      </div>
      
      {/* Gradient overlays - separate layer z-[1] */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-background/90 via-background/60 to-background/95" />
      <div className="absolute inset-0 z-[1] bg-gradient-to-r from-background/70 via-transparent to-background/70" />

      {/* Floating decorative elements with parallax - z-[2] */}
      <div 
        className="absolute z-[2] top-[15%] left-[5%] w-20 h-20 md:w-32 md:h-32 rounded-full bg-primary/10 blur-2xl"
        style={{
          transform: `translateY(${scrollY * -0.1}px) translateX(${mousePosition.x * 40}px)`,
          transition: "transform 0.5s ease-out",
        }}
      />
      <div 
        className="absolute z-[2] bottom-[20%] right-[10%] w-24 h-24 md:w-40 md:h-40 rounded-full bg-primary/15 blur-2xl"
        style={{
          transform: `translateY(${scrollY * 0.15}px) translateX(${mousePosition.x * -30}px)`,
          transition: "transform 0.5s ease-out",
        }}
      />
      <div 
        className="absolute z-[2] top-[40%] right-[20%] w-16 h-16 rounded-full border border-primary/20"
        style={{
          transform: `translateY(${scrollY * 0.08}px) rotate(${scrollY * 0.1}deg)`,
          transition: "transform 0.3s ease-out",
        }}
      />
      <div 
        className="absolute z-[2] bottom-[30%] left-[15%] w-12 h-12 rounded-full border border-primary/15"
        style={{
          transform: `translateY(${scrollY * -0.05}px) rotate(${-scrollY * 0.15}deg)`,
          transition: "transform 0.3s ease-out",
        }}
      />

      {/* Main content - z-10 to stay above everything */}
      <div className="container relative z-10">
        <div 
          className="max-w-4xl mx-auto text-center"
          style={{
            transform: `translateY(${mousePosition.y * -20}px) translateX(${mousePosition.x * -10}px)`,
            transition: "transform 0.4s ease-out",
          }}
        >
          {/* Title with 3D depth */}
          <h1 
            className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-foreground mb-6 animate-fade-in"
            style={{
              transform: `perspective(1000px) translateZ(${mousePosition.y * 10}px)`,
              transition: "transform 0.3s ease-out",
            }}
          >
            {t("hero.title1")}
            <span className="text-primary block mt-2 md:mt-3">{t("hero.title2")}</span>
          </h1>

          {/* Description */}
          <p 
            className="text-lg md:text-xl lg:text-2xl text-foreground font-medium leading-relaxed mb-8 max-w-2xl mx-auto animate-fade-in drop-shadow-sm"
            style={{
              animationDelay: "0.1s",
            }}
          >
            {t("hero.description")}
          </p>

          {/* Features row */}
          <div 
            className="flex flex-wrap justify-center gap-3 md:gap-4 mb-10"
            style={{
              transform: `translateY(${scrollY * 0.02}px)`,
            }}
          >
            {["Digital Marketing", t("hero.feature1"), t("hero.feature2"), t("hero.feature3")].map((feature, index) => (
              <div 
                key={index}
                className="flex items-center gap-2 text-sm md:text-base text-muted-foreground bg-background/50 backdrop-blur-sm px-3 py-2 rounded-full border border-border/50"
                style={{
                  animationDelay: `${0.2 + index * 0.1}s`,
                }}
              >
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span>{feature}</span>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div 
            className="flex justify-center items-center animate-fade-in"
            style={{
              animationDelay: "0.3s",
            }}
          >
            <Button 
              size="lg" 
              className="group px-8"
              onClick={() => {
                document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {t("common.start")}
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        </div>
      </div>

      {/* Bottom fade gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent pointer-events-none" />
    </section>
  );
};

export default HeroSection;
